/**
 * studentActivitiesAdapter — le compte de l'élève connecté (`currentUser.object`) → ce que ses
 * écrans d'activités lisent en plus de la ligne d'une activité (`toUIActivities`, l'adaptateur du
 * parent, lu tel quel).
 *
 * - l'activité brute derrière un identifiant : l'écran d'exécution legacy relit l'activité ENTIÈRE
 *   que le lanceur lui pose (`useLaunchStudentActivity`, `RealizationsListItem.js:92-108`) ;
 * - « a déjà travaillé » : le bloc « Faites » reste affiché dès que l'élève a de l'XP
 *   (`TodoListScreen.js:711`).
 *
 * La carte d'offre des réalisations est celle de « Messages » (`shared/studentOfferAdapter`).
 */
import { type ApiActivity } from "../../parent/Activities/activityAdapter"

/** L'activité telle que le backend la sert : l'exécution legacy la relit en entier. */
export type ApiStudentActivity = ApiActivity & { id: string | number } & Record<string, unknown>

/** Forme lâche du compte : il vient d'un réducteur en JS, tout est optionnel. */
export type ApiStudentActivitiesAccount = {
    id?: string | number
    xp?: number | null
    todo_activities?: ApiActivity[] | null
    activities?: ApiActivity[] | null
    history?: { activities?: ApiActivity[] | null } | null
}

/**
 * L'activité brute derrière un identifiant, cherchée là où l'élève a pu la toucher : sa liste à
 * faire, ses activités faites récentes, puis l'historique paginé des réalisations — l'ordre de
 * `findItemInListWithId(selectedItemId, todos) ?? findItemInListWithId(…, finishedActivities)`
 * (`TodoListScreen.js:260-261`), plus la liste des réalisations.
 */
export function findStudentActivity(
    account: ApiStudentActivitiesAccount | null | undefined,
    id: string | number
): ApiStudentActivity | null {
    const lists = [account?.todo_activities, account?.activities, account?.history?.activities]
    for (const list of lists) {
        const found = (list ?? []).find((activity) => activity?.id != null && String(activity.id) === String(id))
        if (found != null) return found as ApiStudentActivity
    }
    return null
}

/** `user?.xp > 0` — la moitié « XP » de la condition du bloc « Faites » (`TodoListScreen.js:711`). */
export function hasStudentWorked(account: ApiStudentActivitiesAccount | null | undefined): boolean {
    return (account?.xp ?? 0) > 0
}

/**
 * La liste à faire rangée dans l'ordre que l'élève vient de poser (`ids`) : les activités brutes du
 * compte, déplacées sans être recopiées, pour que le réducteur `currentUser` (`setTodoActivities`)
 * les range comme le faisait `onDragEnd` (`TodoListScreen.js:1183-1186`).
 *
 * `null` quand l'ordre ne décrit pas la liste entière, chaque activité une fois : le serveur le
 * refuserait (`updateTodoActivitiesIndexes` exige tous les identifiants), et la liste a changé
 * sous le doigt — une relecture du compte est passée pendant le glisser.
 */
export function reorderTodoActivities(
    todos: readonly ApiActivity[] | null | undefined,
    ids: readonly (string | number)[]
): ApiActivity[] | null {
    const list = todos ?? []
    if (ids.length !== list.length) return null
    const byId = new Map(list.map((activity) => [String(activity?.id), activity]))
    const next: ApiActivity[] = []
    for (const id of ids) {
        const activity = byId.get(String(id))
        if (activity == null) return null
        byId.delete(String(id))
        next.push(activity)
    }
    return next
}

/** Les identifiants de la liste à faire, dans son ordre — le corps de `PUT activities/todo/indexes`. */
export function todoActivityIds(todos: readonly ApiActivity[] | null | undefined): (string | number)[] {
    return (todos ?? []).flatMap((activity) => (activity?.id != null ? [activity.id] : []))
}
